import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PhoneCall, Clock, CheckCircle2, Archive, MessageSquare, User, Package } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending', icon: Clock },
  { value: 'contacted', label: 'Contacted', icon: CheckCircle2 },
  { value: 'closed', label: 'Closed', icon: Archive },
  { value: 'all', label: 'All', icon: null },
];

function statusBadge(status) {
  const map = {
    pending: 'bg-[#0B4F3F]/10 text-[#0B4F3F]',
    contacted: 'bg-[#15803D]/10 text-[#15803D]',
    closed: 'bg-[#5B6675]/10 text-[#5B6675]',
  };
  return `text-[10px] font-bold rounded-full px-2 py-0.5 ${map[status] || 'bg-[#EFF2F7] text-[#5B6675]'}`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function AdminCallbackRequests() {
  const { api } = useAuth();
  const [filter, setFilter] = useState('pending');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, contacted: 0, closed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/callback-requests', {
        params: { status: filter === 'all' ? undefined : filter },
      });
      setItems(data.requests || []);
      setCounts(data.counts || { pending: 0, contacted: 0, closed: 0, total: 0 });
    } catch {
      toast.error('Failed to load callback requests');
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.put(`/admin/callback-requests/${id}`, {
        status,
        admin_notes: notes[id] || undefined,
      });
      toast.success(`Marked as ${status}`);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const callUser = (phone) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  return (
    <div className="space-y-4" data-testid="admin-callback-requests">
      {/* Stat row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <Clock className="w-4 h-4 mx-auto text-[#0B4F3F] mb-1" />
          <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{counts.pending}</p>
          <p className="text-[10px] text-[#5B6675]">Pending</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <CheckCircle2 className="w-4 h-4 mx-auto text-[#15803D] mb-1" />
          <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{counts.contacted}</p>
          <p className="text-[10px] text-[#5B6675]">Contacted</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <Archive className="w-4 h-4 mx-auto text-[#5B6675] mb-1" />
          <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{counts.closed}</p>
          <p className="text-[10px] text-[#5B6675]">Closed</p>
        </div>
      </div>

      {/* Filter tabs */}
      <Tabs value={filter} onValueChange={setFilter} className="w-full">
        <TabsList className="w-full bg-[#EFF2F7] rounded-xl p-1 h-auto">
          {STATUS_TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={`requests-tab-${t.value}`}
              className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B4F3F] data-[state=active]:shadow-sm"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* List */}
      {loading ? (
        Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 animate-pulse space-y-3">
            <div className="h-4 bg-[#EFF2F7] rounded w-1/2" />
            <div className="h-3 bg-[#EFF2F7] rounded w-3/4" />
          </div>
        ))
      ) : items.length === 0 ? (
        <div className="text-center py-12">
          <PhoneCall className="w-10 h-10 mx-auto text-[#5B6675]/30 mb-3" />
          <p className="text-sm text-[#5B6675]">No {filter !== 'all' ? filter : ''} requests</p>
        </div>
      ) : (
        items.map((r) => (
          <div
            key={r.id}
            data-testid={`callback-row-${r.id}`}
            className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0B1620] flex items-center gap-1.5 truncate">
                  <Package className="w-3.5 h-3.5 text-[#0B4F3F] flex-shrink-0" />
                  {r.post_title || 'Listing'}
                  {r.post_price && (
                    <span className="text-[11px] text-[#5B6675] font-normal">
                      · ₹{r.post_price}/{r.post_unit}
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-[#5B6675] mt-0.5">{formatDate(r.created_at)}</p>
              </div>
              <span className={statusBadge(r.status)}>{r.status.toUpperCase()}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="bg-[#EFF2F7] rounded-lg p-2">
                <p className="text-[10px] uppercase tracking-wide text-[#5B6675] flex items-center gap-1">
                  <User className="w-3 h-3" /> Vendor
                </p>
                <p className="text-[#0B1620] font-medium truncate">{r.user_name}</p>
                {r.user_phone && (
                  <button
                    type="button"
                    data-testid={`call-user-${r.id}`}
                    onClick={() => callUser(r.user_phone)}
                    className="text-[#0B4F3F] font-semibold underline-offset-2 hover:underline"
                  >
                    {r.user_phone}
                  </button>
                )}
              </div>
              <div className="bg-[#EFF2F7] rounded-lg p-2">
                <p className="text-[10px] uppercase tracking-wide text-[#5B6675] flex items-center gap-1">
                  <PhoneCall className="w-3 h-3" /> Farmer
                </p>
                <p className="text-[#0B1620] font-medium truncate">{r.farmer_name || '—'}</p>
                {r.farmer_phone && (
                  <button
                    type="button"
                    data-testid={`call-farmer-${r.id}`}
                    onClick={() => callUser(r.farmer_phone)}
                    className="text-[#0B4F3F] font-semibold underline-offset-2 hover:underline"
                  >
                    {r.farmer_phone}
                  </button>
                )}
              </div>
            </div>

            {r.note && (
              <div className="text-xs bg-[#F6F8FB] rounded-lg p-2 border border-[#E4E8EF]">
                <p className="text-[10px] uppercase tracking-wide text-[#5B6675] mb-0.5">User note</p>
                <p className="text-[#0B1620]">{r.note}</p>
              </div>
            )}

            {r.admin_notes && (
              <div className="text-xs bg-[#F6F8FB] rounded-lg p-2 border border-[#E4E8EF]">
                <p className="text-[10px] uppercase tracking-wide text-[#5B6675] mb-0.5 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> Internal notes
                </p>
                <p className="text-[#0B1620]">{r.admin_notes}</p>
              </div>
            )}

            {r.status !== 'closed' && (
              <div className="space-y-2 pt-2 border-t border-[#E4E8EF]">
                <Textarea
                  data-testid={`request-notes-${r.id}`}
                  placeholder="Add internal notes (optional)"
                  value={notes[r.id] || ''}
                  onChange={(e) => setNotes((prev) => ({ ...prev, [r.id]: e.target.value }))}
                  rows={2}
                  className="text-xs bg-[#F1F5FA] border-[#E4E8EF] rounded-lg resize-none"
                />
                <div className="flex gap-2">
                  {r.status === 'pending' && (
                    <Button
                      data-testid={`mark-contacted-btn-${r.id}`}
                      onClick={() => updateStatus(r.id, 'contacted')}
                      disabled={busyId === r.id}
                      size="sm"
                      className="flex-1 h-9 bg-[#15803D] hover:bg-[#15803D]/90 text-[#F6F8FB] rounded-lg text-xs"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Contacted
                    </Button>
                  )}
                  <Button
                    data-testid={`mark-closed-btn-${r.id}`}
                    onClick={() => updateStatus(r.id, 'closed')}
                    disabled={busyId === r.id}
                    size="sm"
                    variant="outline"
                    className="flex-1 h-9 border-[#5B6675]/30 text-[#5B6675] hover:bg-[#EFF2F7] rounded-lg text-xs"
                  >
                    <Archive className="w-3.5 h-3.5 mr-1" /> Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
